import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section className="relative h-[520px] md:h-[620px] w-full overflow-hidden">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.imageUrl}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-4 md:px-6">
        <div className="max-w-3xl w-full">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`transition-all duration-500 ${
                index === currentSlide
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
              }`}
            >
              {index === currentSlide && (
                <>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="px-3 py-1 text-xs font-medium bg-accent-500 text-white rounded-full">
                      {slide.category}
                    </span>
                    <span className="flex items-center gap-1 text-yellow-400 text-sm">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill"></i>
                      </div>
                      {slide.rating}
                    </span>
                  </div>
                  <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    {slide.title}
                  </h1>
                  <p className="text-white/80 text-base md:text-lg mb-8">
                    {slide.subtitle}
                  </p>
                  <Link
                    to={slide.link}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-circle-fill"></i>
                    </div>
                    立即观看
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                index === currentSlide
                  ? 'bg-primary-500 w-8'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`切换到第 ${index + 1} 张`}
            />
          ))}
        </div>

        <button
          onClick={() => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length)}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}